import { UploadProgress } from '../UploadProgress';
import { ExportLayoutProgressModal } from '../ExportLayout';
import { ProgressBar, FileInfoDisplay, StateIndicator } from './components';
import { ConfirmModal } from '../ui';
import { ErrorModal } from '../feedback/ErrorModal';
import { LockRecoveryModal } from '../LockRecoveryModal';
import { SaveProgressModalProps } from './types';
import { useSaveController } from './hooks/useSaveController';
import { Button } from '../ui';
import { sendToTerminal } from '../../lib/terminalLogger';
import { useState, useCallback } from 'react';
import { corelCommunication } from '../../lib/corel/communication';
import { 
  CopyIcon as Copy,
  CheckCircle2Icon as CheckCircle2,
  MessageSquareIcon as MessageSquare
} from '../icons';

export function SaveProgressModal({ onComplete, initialMetadataSnapshot }: SaveProgressModalProps) {
  const [showLockRecovery, setShowLockRecovery] = useState(false);
  
  // ✅ Estados para funcionalidade de copiar mensagem (inline no sucesso)
  const [copied, setCopied] = useState(false);
  const [fechandoDocumento, setFechandoDocumento] = useState(false);
  
  let hookResult;
  try {
    hookResult = useSaveController({ onComplete, initialMetadataSnapshot });
  } catch (err) {
    sendToTerminal('❌ [SAVE_PROGRESS_MODAL] Erro ao executar useSaveController', {
      error: err instanceof Error ? err.message : String(err)
    });
    throw err;
  }
  
  const {
    saveState,
    progress,
    fileInfo,
    showMetadataModal,
    // showClientMessage, // ✅ REMOVIDO: não usado mais (mensagem inline)
    errorMessage,
    isClosing,
    clientMessage,
    needConfirmUpdate,
    handleClose,
    // handleShowClientMessage, // ✅ REMOVIDO: não usado mais (mensagem inline)
    handleExportComplete,
    confirmUpdate,
    cancelUpdate,
    getErrorTitle,
    handleRetry,
  } = hookResult;

  // ✅ Função para fechar o documento ativo no CorelDRAW (similar ao MessageModal)
  const fecharDocumento = useCallback(() => {
    if (!window.chrome?.webview) {
      onComplete();
      return;
    }

    setFechandoDocumento(true);
    const requestId = `fechar-doc-${Date.now()}`;

    const mensagem = {
      action: "fechar-documento",
      data: {},
      requestId
    } as any;

    let alreadyClosed = false;
    const finalizeAndClose = () => {
      if (alreadyClosed) return;
      alreadyClosed = true;
      setFechandoDocumento(false);
      onComplete();
    };

    const fallbackTimer = setTimeout(() => {
      finalizeAndClose();
    }, 900);

    const handleResponse = (event: any) => {
      try {
        let resposta;
        if (typeof event.data === 'string') {
          resposta = JSON.parse(event.data);
        } else {
          resposta = event.data;
        }

        const isOurResponse = resposta?.requestId === requestId || 
                              resposta?.action === 'fechar-documento' || 
                              resposta?.result?.acao === 'fechar-documento';
        if (isOurResponse) {
          try { clearTimeout(fallbackTimer); } catch {}

          if (window.chrome?.webview) {
            try {
              // @ts-ignore
              window.chrome.webview.removeEventListener('message', handleResponse);
            } catch {}
          }

          sendToTerminal('✅ [SAVE_PROGRESS] SUCCESS recebido do CorelDRAW, aguardando 200ms');
          setTimeout(() => {
            sendToTerminal('🚪 [SAVE_PROGRESS] Fechando modal após delay');
            finalizeAndClose();
          }, 200);
        }
      } catch {}
    };

    try {
      window.chrome.webview.addEventListener('message', handleResponse);
    } catch {}

    sendToTerminal('📤 [SAVE_PROGRESS:FECHAR] Enviando fechar-documento', { requestId });

    try {
      // @ts-ignore
      corelCommunication.sendActionGuarded(mensagem.action, mensagem.data);
      sendToTerminal('✅ [SAVE_PROGRESS:FECHAR] Mensagem enviada com sucesso');
    } catch (e) {
      sendToTerminal('❌ [SAVE_PROGRESS:FECHAR] Erro ao enviar', {
        error: e instanceof Error ? e.message : String(e)
      });
    }
  }, [onComplete]);

  // ✅ Função para copiar mensagem e fechar documento
  const handleCopyAndClose = useCallback(async () => {
    if (!clientMessage) {
      onComplete();
      return;
    }

    try {
      await navigator.clipboard.writeText(clientMessage);
      setCopied(true);
      sendToTerminal('✅ [SAVE_PROGRESS] Mensagem copiada para clipboard');

      // Mostrar feedback "Copiado!" por 1 segundo, então fechar documento
      setTimeout(() => {
        fecharDocumento();
      }, 1000);
    } catch (err) {
      sendToTerminal('❌ [SAVE_PROGRESS] Erro ao copiar mensagem', {
        error: err instanceof Error ? err.message : String(err)
      });
      // Mesmo com erro, tenta fechar
      fecharDocumento();
    }
  }, [clientMessage, fecharDocumento, onComplete]);

  // Renderização do modal - silencioso

  if (saveState === 'exporting') {
    return (
      <ExportLayoutProgressModal 
        onComplete={handleExportComplete}
      />
    );
  }

  if (saveState === 'uploading') {
    return (
      <UploadProgress 
        onClose={onComplete}
        onRetry={() => { /* manter comportamento atual */ }}
      />
    );
  }
  
  // ✅ REMOVIDO: MessageModal separada - agora mensagem aparece inline no sucesso
  // A funcionalidade foi movida para dentro do modal principal (saveState === 'success')

  if (needConfirmUpdate) {
    return (
      <ConfirmModal
        title="Arquivo já existe"
        message={"Já existe um histórico de salvamento desse arquivo.\n\nRealmente deseja substituir?"}
        confirmLabel="OK"
        cancelLabel="Cancelar"
        onConfirm={confirmUpdate}
        onCancel={cancelUpdate}
      />
    );
  }

  if (saveState === 'error' && errorMessage) {
    return (
      <ErrorModal
        open={true}
        title={getErrorTitle(errorMessage)}
        message={errorMessage}
        onClose={onComplete}
        onRetry={handleRetry}
      />
    );
  }

  if (saveState === 'cancelled') {
    onComplete();
    return null;
  }
  
  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-3">
        {!showMetadataModal && (
          <div 
            className={`bg-navy-800 rounded-xl w-full max-w-[320px] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'} shadow-2xl border border-navy-600/50 overflow-hidden`}
          >
            {/* Header otimizado para 290px */}
            <div className="flex justify-between items-center p-4 border-b border-navy-700/50 bg-gradient-to-r from-navy-800 to-navy-700/30">
              <StateIndicator state={saveState} errorMessage={errorMessage} />
              <Button
                variant="ghost"
                size="xs"
                onClick={handleClose}
              >
                Fechar
              </Button>
            </div>
            
            {/* Conteúdo otimizado para 290px */}
            <div className="p-4 space-y-4">
              <FileInfoDisplay 
                fileName={fileInfo.fileName} 
                isLoading={saveState === 'saving'}
              />
              
              {saveState !== 'success' && (
                <ProgressBar progress={progress} state={saveState} showPercentage={true} />
              )}
              
              {/* ✅ UNIFICADO: Sucesso + Mensagem + Copiar em uma única view */}
              {saveState === 'success' && (
                <div className="space-y-3">
                  {/* Indicador de sucesso */}
                  <div className="flex items-center justify-center gap-2 text-emerald-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-medium">Salvo com sucesso!</span>
                  </div>
                  
                  {/* Label da mensagem */}
                  <div className="flex items-center gap-2 text-gray-400 text-xs">
                    <MessageSquare className="w-3 h-3" />
                    <span>Mensagem para o Cliente</span>
                  </div>
                  
                  {/* Área de mensagem scrollável */}
                  <div className="bg-gradient-to-r from-navy-700/40 to-navy-600/20 rounded-lg p-3 border border-navy-600/30 font-mono text-xs text-gray-300 whitespace-pre-wrap h-40 overflow-y-auto">
                    {clientMessage || 'Mensagem não disponível'}
                  </div>
                  
                  {/* Botão Copiar e Fechar */}
                  <button
                    onClick={handleCopyAndClose}
                    disabled={copied || fechandoDocumento}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${
                      copied 
                        ? 'bg-emerald-500 text-white' 
                        : fechandoDocumento
                          ? 'bg-gray-500 text-white cursor-not-allowed'
                          : 'bg-yellow-400 text-navy-900 hover:bg-yellow-500'
                    }`}
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Copiado! Fechando...
                      </>
                    ) : fechandoDocumento ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 animate-pulse" />
                        Fechando documento...
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copiar e Fechar Documento
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {showMetadataModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-navy-800 border border-navy-700 rounded-lg px-5 py-4 shadow-xl text-sm text-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-yellow-400 animate-pulse"></div>
                <div>
                  <div className="font-semibold">Coletando metadados do documento…</div>
                  <div className="text-xs text-gray-400">Aguarde, vamos enviar para a API em seguida.</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lock Recovery Modal */}
      <LockRecoveryModal
        visible={showLockRecovery}
        onHide={() => setShowLockRecovery(false)}
        onRecovered={() => {
          sendToTerminal('✅ [SAVE_PROGRESS_MODAL] Lock recuperado com sucesso');
        }}
        context="save"
      />
    </>
  );
}


